// /admin/mutui — Mutui: i prospetti di sostenibilità. **Solo admin** (il server risponde 403 a tutti gli altri:
// backend/mutui.py). A sinistra l'elenco, a destra il prospetto aperto.
//
// Tre cose da sapere leggendo questo file:
// - il conto **non si fa qui**. Ogni modifica va a POST /mutui/calcolo e i numeri tornano dal server, così la
//   formula resta in un posto solo (backend/calcolo_mutuo.py) invece di esistere in due versioni che un giorno
//   daranno risposte diverse;
// - il tasso non si sceglie: arriva dal server, uno per durata, e viene da mkconsulting.it;
// - il salvataggio è esplicito. Finché ci sono modifiche non salvate la pagina lo dice e chiede conferma prima
//   di lasciarle perdere.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { euro } from '../../utils/scheda';
import { giornoEMese, leggiDataUtc } from '../../utils/percorso';
import Icona from '../../components/cliente/Icone';
import { errore } from '../../components/scheda/comuni';
import './mutui.css';

const VUOTO = {
  titolo: '', note: '', cliente_id: null, figli: 0, anticipo: null, durata_anni: 25,
  persone: [{ etichetta: '', netto_mensile: null, mensilita: 12, eta: null }],
  prestiti: [],
};
const MENSILITA = [12, 13, 14];
const n = (v) => (v === '' || v === null || v === undefined ? null : Number(String(v).replace(',', '.')));
const soldi = (v) => (v === null || v === undefined ? '—' : euro(Math.round(v)));
const quando = (iso) => (iso ? giornoEMese(leggiDataUtc(iso)) : '');

// I dati che il server vuole: solo gli ingressi, mai i risultati
const perIlServer = (p) => ({
  titolo: p.titolo, note: p.note, figli: n(p.figli) || 0, anticipo: n(p.anticipo),
  durata_anni: n(p.durata_anni) || 25,
  persone: (p.persone || []).map((x) => ({ etichetta: x.etichetta, netto_mensile: n(x.netto_mensile), mensilita: x.mensilita, eta: n(x.eta) })),
  prestiti: (p.prestiti || []).map((x) => ({ descrizione: x.descrizione, rata: n(x.rata), mesi_residui: n(x.mesi_residui), da_estinguere: !!x.da_estinguere })),
});

// ---------------------------------------------------------------- il riquadro del tasso
const Tasso = ({ stato }) => {
  if (!stato) return null;
  const grave = stato.origine === 'assente' || stato.vecchio;
  if (!stato.avviso && !stato.aggiornato_il) return null;
  return (
    <div className={`mu-tasso${grave ? ' grave' : ''}`} data-testid="tasso-stato">
      <Icona nome={grave ? 'avviso' : 'fatto'} />
      <span>
        {stato.avviso || `tasso di riferimento, aggiornato il ${stato.aggiornato_il?.split('-').reverse().join('/')}`}
      </span>
    </div>
  );
};

// ---------------------------------------------------------------- i risultati
const Risultati = ({ calcolo }) => {
  if (!calcolo) return null;
  const c = calcolo;
  return (
    <section className="card mu-risultati" data-testid="risultati">
      <div className="mu-griglia">
        <div><small>Reddito mensile</small><b>{soldi(c.reddito_mensile)}<i>al mese</i></b></div>
        <div><small>Rate dei prestiti in corso</small><b>{soldi(c.rate_esistenti)}<i>al mese</i></b></div>
        <div><small>Rapporto massimo</small><b>{String(c.soglia_rata_reddito).replace('.', ',')} %<i>del reddito</i></b></div>
      </div>
      <div className="mu-rata" data-testid="rata-massima">
        <div>
          <small>Rata massima</small>
          <b>{soldi(c.rata_massima)}</b>
          {c.messaggio && <span>{c.messaggio}</span>}
        </div>
        <div>
          <small>Mutuo massimo{c.tasso !== null ? ` · ${c.durata_anni} anni al ${String(c.tasso).replace('.', ',')}%` : ''}</small>
          <b data-testid="importo">{c.tasso === null ? '—' : soldi(c.importo_mutuo_mostrato)}</b>
          {c.durata_ridotta && <span className="avv">durata ridotta a {c.durata_anni} anni per l&apos;età</span>}
        </div>
      </div>
      <p className="mu-resta" data-testid="resta-per-vivere">
        Resta per vivere dopo la rata: <b>{soldi(c.resta_per_vivere)}</b>
      </p>
      {c.avviso_resta_per_vivere && (
        <p className="mu-attenzione" data-testid="avviso-resta">{c.avviso_resta_per_vivere}</p>
      )}
      {c.prezzo_massimo_immobile !== null && (
        <div className="mu-prezzo" data-testid="prezzo-massimo">
          <div><small>Casa fino a</small><b>{soldi(c.prezzo_massimo_mostrato)}</b></div>
          <div><small>di cui mutuo</small><b>{soldi(c.mutuo_effettivo_mostrato)}</b></div>
          <div><small>anticipo</small><b>{soldi(c.anticipo)}</b></div>
          <p>{c.nota_spese}</p>
        </div>
      )}
      {c.scenari?.length > 0 && (
        <div className="mu-scenari">
          <h3>Se cambia il tasso</h3>
          <div className="mu-tabella" role="table" aria-label="Importo del mutuo per durata e tasso">
            <div role="row" className="t"><span role="columnheader">durata</span>
              {c.scenari[0].celle.map((_, i) => (
                <span role="columnheader" key={i}>{i === 0 ? 'tasso di oggi' : `+${i} punt${i === 1 ? 'o' : 'i'}`}</span>
              ))}
            </div>
            {c.scenari.map((riga) => (
              <div role="row" key={riga.anni}>
                <span role="cell"><b>{riga.anni} anni</b></span>
                {riga.celle.map((cella) => (
                  <span role="cell" key={cella.tasso}>
                    {soldi(cella.importo_mostrato)}<i>{String(cella.tasso).replace('.', ',')}%</i>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="mu-avviso" data-testid="avviso-banca">{c.avviso}</p>
    </section>
  );
};

// ---------------------------------------------------------------- il modulo
const Modulo = ({ dati, cambia, calcolo }) => {
  const durate = calcolo?.durate_possibili || [];
  const persona = (i, campo, valore) => cambia({ ...dati, persone: dati.persone.map((p, k) => (k === i ? { ...p, [campo]: valore } : p)) });
  const prestito = (i, campo, valore) => cambia({ ...dati, prestiti: dati.prestiti.map((p, k) => (k === i ? { ...p, [campo]: valore } : p)) });
  return (
    <section className="card mu-modulo" data-testid="modulo">
      <label className="mu-titolo">Titolo
        <input value={dati.titolo} onChange={(e) => cambia({ ...dati, titolo: e.target.value })}
          placeholder="Rossi - telefonata 15/10" maxLength={200} aria-label="Titolo del prospetto" />
      </label>

      <h3>Chi porta reddito</h3>
      {dati.persone.map((p, i) => (
        <div className="mu-riga" key={i}>
          <input value={p.etichetta || ''} onChange={(e) => persona(i, 'etichetta', e.target.value)}
            placeholder={i === 0 ? 'Marito' : 'Moglie'} aria-label={`Chi è la persona ${i + 1}`} maxLength={60} />
          <input value={p.netto_mensile ?? ''} onChange={(e) => persona(i, 'netto_mensile', e.target.value)}
            inputMode="decimal" placeholder="netto al mese" aria-label={`Netto mensile della persona ${i + 1}`} />
          <select value={p.mensilita} onChange={(e) => persona(i, 'mensilita', Number(e.target.value))}
            aria-label={`Mensilità della persona ${i + 1}`}>
            {MENSILITA.map((m) => <option key={m} value={m}>{m} mensilità</option>)}
          </select>
          <input value={p.eta ?? ''} onChange={(e) => persona(i, 'eta', e.target.value)}
            inputMode="numeric" placeholder="età" aria-label={`Età della persona ${i + 1}`} />
          {dati.persone.length > 1 && (
            <button type="button" className="btn xs" aria-label={`Togli la persona ${i + 1}`}
              onClick={() => cambia({ ...dati, persone: dati.persone.filter((_, k) => k !== i) })}><Icona nome="chiudi" /></button>
          )}
        </div>
      ))}
      {dati.persone.length < 4 && (
        <button type="button" className="btn sm" onClick={() => cambia({ ...dati, persone: [...dati.persone, { etichetta: '', netto_mensile: null, mensilita: 12, eta: null }] })}>+ Persona</button>
      )}

      <h3>Prestiti in corso</h3>
      {dati.prestiti.length === 0 && <p className="vuoto">Nessun prestito.</p>}
      {dati.prestiti.map((p, i) => (
        <div className="mu-riga prestito" key={i}>
          <input value={p.descrizione || ''} onChange={(e) => prestito(i, 'descrizione', e.target.value)}
            placeholder="auto" aria-label={`Che prestito è, riga ${i + 1}`} maxLength={60} />
          <input value={p.rata ?? ''} onChange={(e) => prestito(i, 'rata', e.target.value)}
            inputMode="decimal" placeholder="rata" aria-label={`Rata del prestito ${i + 1}`} />
          <input value={p.mesi_residui ?? ''} onChange={(e) => prestito(i, 'mesi_residui', e.target.value)}
            inputMode="numeric" placeholder="mesi che restano" aria-label={`Mesi che restano del prestito ${i + 1}`} />
          <label className="mu-spunta">
            <input type="checkbox" checked={!!p.da_estinguere} onChange={(e) => prestito(i, 'da_estinguere', e.target.checked)} />
            si estingue
          </label>
          <button type="button" className="btn xs" aria-label={`Togli il prestito ${i + 1}`}
            onClick={() => cambia({ ...dati, prestiti: dati.prestiti.filter((_, k) => k !== i) })}><Icona nome="chiudi" /></button>
        </div>
      ))}
      <button type="button" className="btn sm" onClick={() => cambia({ ...dati, prestiti: [...dati.prestiti, { descrizione: '', rata: null, mesi_residui: null, da_estinguere: false }] })}>+ Prestito</button>

      <h3>Il resto</h3>
      <div className="mu-riga">
        <label>Figli a carico
          <input value={dati.figli ?? 0} onChange={(e) => cambia({ ...dati, figli: e.target.value })} inputMode="numeric" aria-label="Figli a carico" />
        </label>
        <label>Anticipo
          <input value={dati.anticipo ?? ''} onChange={(e) => cambia({ ...dati, anticipo: e.target.value })} inputMode="decimal" placeholder="facoltativo" aria-label="Anticipo disponibile" />
        </label>
        <label>Durata
          <select value={dati.durata_anni} onChange={(e) => cambia({ ...dati, durata_anni: Number(e.target.value) })} aria-label="Durata del mutuo">
            {(durate.length ? durate : [dati.durata_anni]).map((a) => <option key={a} value={a}>{a} anni</option>)}
          </select>
        </label>
      </div>
      <label className="mu-note">Note
        <textarea value={dati.note || ''} onChange={(e) => cambia({ ...dati, note: e.target.value })} rows={2} maxLength={2000} aria-label="Note" />
      </label>
    </section>
  );
};

// ---------------------------------------------------------------- collega a cliente
const Collega = ({ prospetto, onFatto, onChiudi }) => {
  const [cerca, setCerca] = useState('');
  const [trovati, setTrovati] = useState([]);
  const [cercato, setCercato] = useState(false);
  // Il filtro lo fa il server (GET /mutui/cerca-clienti): qui non si scarica più l'anagrafica intera. Prima la
  // pagina chiamava GET /clienti con un parametro che quella rotta non conosce, FastAPI lo buttava via e
  // tornavano tutti i clienti: scrivendo «ahmed» si vedevano i primi otto, che non c'entravano (15/10/2026).
  useEffect(() => {
    const q = cerca.trim();
    if (q.length < 2) { setTrovati([]); setCercato(false); return undefined; }
    const t = setTimeout(() => {
      axios.get(`${API}/mutui/cerca-clienti`, { params: { q } })
        .then((r) => { setTrovati(r.data?.clienti || []); setCercato(true); })
        .catch(() => { setTrovati([]); setCercato(true); });
    }, 250);
    return () => clearTimeout(t);
  }, [cerca]);
  const scegli = async (id) => {
    try {
      const r = await axios.put(`${API}/mutui/prospetti/${prospetto.id}/cliente`, { cliente_id: id });
      toast.success(id ? 'Collegato' : 'Scollegato');
      onFatto(r.data.prospetto);
    } catch (e) { errore(e); }
  };
  return (
    <div className="mu-collega" data-testid="collega">
      <input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Cerca un cliente per nome" aria-label="Cerca un cliente" autoFocus />
      <p className="vuoto">Se la persona non è ancora cliente, lascia il prospetto non collegato: da qui non si crea nessun cliente.</p>
      <div className="mu-trovati">
        {trovati.map((c) => (
          <button type="button" key={c.id} className="mu-trovato" onClick={() => scegli(c.id)}>
            <b>{[c.nome, c.cognome].filter(Boolean).join(' ')}</b>
            {c.distingui && <span>{c.distingui}</span>}
          </button>
        ))}
        {cercato && trovati.length === 0 && <p className="vuoto" data-testid="nessun-cliente">Nessun cliente trovato</p>}
      </div>
      <div className="mu-azioni">
        {prospetto.cliente_id && <button type="button" className="btn sm rosso-vuoto" onClick={() => scegli('')}>Scollega</button>}
        <button type="button" className="btn sm" onClick={onChiudi}>Chiudi</button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- la pagina
const Mutui = () => {
  const { id } = useParams();
  const vai = useNavigate();
  const [elenco, setElenco] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [collegati, setCollegati] = useState('');
  const [prospetto, setProspetto] = useState(null);   // quello salvato
  const [dati, setDati] = useState(null);             // quello che si sta scrivendo
  const [calcolo, setCalcolo] = useState(null);
  const [tasso, setTasso] = useState(null);
  const [collega, setCollega] = useState(false);
  const [elimina, setElimina] = useState(false);
  const [salvo, setSalvo] = useState(false);
  const ultimo = useRef(0);

  const caricaElenco = useCallback(() => {
    axios.get(`${API}/mutui/prospetti`, { params: { cerca: filtro, collegati } })
      .then((r) => { setElenco(r.data.prospetti || []); setTasso(r.data.tasso); })
      .catch(errore);
  }, [filtro, collegati]);
  useEffect(() => { caricaElenco(); }, [caricaElenco]);

  useEffect(() => {
    if (!id) { setProspetto(null); setDati(null); setCalcolo(null); return; }
    axios.get(`${API}/mutui/prospetti/${id}`)
      .then((r) => { setProspetto(r.data.prospetto); setDati(r.data.prospetto); setCalcolo(r.data.calcolo); })
      .catch(errore);
  }, [id]);

  // il conto si rifà sul server a ogni modifica, con un respiro: la formula resta in un posto solo
  useEffect(() => {
    if (!dati) return undefined;
    const mio = ++ultimo.current;
    const t = setTimeout(() => {
      axios.post(`${API}/mutui/calcolo`, perIlServer(dati))
        .then((r) => { if (mio === ultimo.current) setCalcolo(r.data.calcolo); })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [dati]);

  const cambiato = useMemo(
    () => Boolean(dati && prospetto && JSON.stringify(perIlServer(dati)) !== JSON.stringify(perIlServer(prospetto))),
    [dati, prospetto],
  );
  // uscire dalla pagina con modifiche non salvate chiede conferma
  useEffect(() => {
    if (!cambiato) return undefined;
    const avvisa = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', avvisa);
    return () => window.removeEventListener('beforeunload', avvisa);
  }, [cambiato]);

  const apri = (nuovoId) => {
    if (cambiato && !window.confirm('Ci sono modifiche non salvate: le lascio perdere?')) return;
    vai(nuovoId ? `/admin/mutui/${nuovoId}` : '/admin/mutui');
  };

  const salva = async () => {
    setSalvo(true);
    try {
      const r = await axios.put(`${API}/mutui/prospetti/${prospetto.id}`, perIlServer(dati));
      setProspetto(r.data.prospetto); setDati(r.data.prospetto); setCalcolo(r.data.calcolo);
      toast.success('Salvato');
      caricaElenco();
    } catch (e) { errore(e); } finally { setSalvo(false); }
  };

  const nuovo = async () => {
    if (cambiato && !window.confirm('Ci sono modifiche non salvate: le lascio perdere?')) return;
    const titolo = window.prompt('Titolo del prospetto', 'Nuovo prospetto');
    if (!titolo?.trim()) return;
    try {
      const r = await axios.post(`${API}/mutui/prospetti`, { ...perIlServer(VUOTO), titolo: titolo.trim() });
      caricaElenco();
      vai(`/admin/mutui/${r.data.prospetto.id}`);
    } catch (e) { errore(e); }
  };

  const duplica = async () => {
    try {
      const r = await axios.post(`${API}/mutui/prospetti/${prospetto.id}/duplica`);
      toast.success('Copia creata');
      caricaElenco();
      vai(`/admin/mutui/${r.data.prospetto.id}`);
    } catch (e) { errore(e); }
  };

  const eliminaDavvero = async () => {
    try {
      await axios.delete(`${API}/mutui/prospetti/${prospetto.id}`);
      toast.success('Eliminato');
      setElimina(false);
      caricaElenco();
      vai('/admin/mutui');
    } catch (e) { errore(e); }
  };

  return (
    <main className="st wrap mu" data-testid="pagina-Mutui">
      <div className="mu-testa">
        <div>
          <div className="k">Solo admin</div>
          <h1>Mutui</h1>
          <p>Quanto può pagare una famiglia, e quanto mutuo fa quella rata.</p>
        </div>
        <button type="button" className="btn p" onClick={nuovo}>Nuovo prospetto</button>
      </div>
      <Tasso stato={calcolo?.tasso_stato || tasso} />

      <div className="mu-lay">
        <aside className="mu-elenco" data-testid="elenco">
          <input value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="Cerca per titolo o cliente" aria-label="Cerca un prospetto" />
          <div className="mu-filtri" role="group" aria-label="Collegati o no">
            {[['', 'Tutti'], ['si', 'Collegati'], ['no', 'Non collegati']].map(([v, etichetta]) => (
              <button type="button" key={v} className={`chip${collegati === v ? ' on' : ''}`} aria-pressed={collegati === v}
                onClick={() => setCollegati(v)}>{etichetta}</button>
            ))}
          </div>
          {elenco.length === 0 && <p className="vuoto">Nessun prospetto.</p>}
          {elenco.map((p) => (
            <button type="button" key={p.id} className={`mu-voce${p.id === id ? ' on' : ''}`} data-testid={`voce-${p.id}`} onClick={() => apri(p.id)}>
              <b>{p.titolo}</b>
              <span>{p.cliente ? p.cliente.nome : 'non collegato'}</span>
              <i>{soldi(p.rata_massima)}/mese · {soldi(p.importo_mutuo_mostrato)}</i>
              <small>{quando(p.aggiornato_il)}</small>
            </button>
          ))}
        </aside>

        <div className="mu-scheda">
          {!dati && <p className="vuoto">Scegli un prospetto a sinistra, o creane uno nuovo.</p>}
          {dati && (
            <>
              <div className="mu-barra">
                <div>
                  <b>{prospetto?.cliente ? `Collegato a ${prospetto.cliente.nome}` : 'Non collegato a nessun cliente'}</b>
                  <span>
                    {prospetto?.aggiornato_il ? `ultimo salvataggio: ${quando(prospetto.aggiornato_il)}, da ${prospetto.aggiornato_da?.nome || '—'}` : ''}
                    {cambiato ? ' · modifiche non salvate' : ''}
                  </span>
                </div>
                <div className="mu-azioni">
                  <button type="button" className="btn sm p" onClick={salva} disabled={!cambiato || salvo}>{salvo ? 'Salvo…' : 'Salva'}</button>
                  <button type="button" className="btn sm" onClick={() => setCollega(true)}>{prospetto?.cliente ? 'Cambia cliente' : 'Collega a cliente'}</button>
                  <button type="button" className="btn sm" onClick={duplica}>Duplica</button>
                  <button type="button" className="btn sm rosso-vuoto" onClick={() => setElimina(true)}>Elimina</button>
                </div>
              </div>
              {collega && <Collega prospetto={prospetto} onChiudi={() => setCollega(false)}
                onFatto={(p) => { setCollega(false); setProspetto({ ...prospetto, ...p }); caricaElenco(); }} />}
              {elimina && (
                <div className="mu-elimina" role="alertdialog" aria-label="Eliminare il prospetto?" data-testid="conferma-elimina">
                  <p><b>{prospetto.titolo}</b> viene eliminato <b>per sempre</b>: non va nel cestino e non si recupera.</p>
                  <div className="mu-azioni">
                    <button type="button" className="btn sm" onClick={() => setElimina(false)}>Annulla</button>
                    <button type="button" className="btn sm rosso" onClick={eliminaDavvero}>Elimina per sempre</button>
                  </div>
                </div>
              )}
              <Risultati calcolo={calcolo} />
              <Modulo dati={dati} cambia={setDati} calcolo={calcolo} />
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default Mutui;
